import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import BrowserOnly from 'components/consumer/BrowserOnly';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import JBImage from 'components/essentials/JBImage';
import PDPDesktopFullScreenGallery2 from 'components/consumer/PDP/PDPDesktopFullScreenGallery2';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import Placeholder from 'components/consumer/Placeholder';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useScreen from 'hooks/useScreen';
import getImageUrlWithoutQuery from 'commons/getImageUrlWithoutQuery';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import getProductImageBaseImgixParams from 'commons/getProductImageBaseImgixParams';
import BarbieLogo from 'assets/images/made-for-barbie-logo.png';
import BipocLogo from 'assets/images/bipoc-badge.png';
import useIsBrowser from 'hooks/useIsBrowser';
import useBrokenImageUrls from 'global-state/brokenImageUrls/useBrokenImageUrls';
import { GALLERY_IMAGE_LOCATION, GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';
import usePDPGalleryInteractedEvent from 'data-hooks/useEvents/usePDPGalleryInteractedEvent';
import PDPDesktopThumbnails from './PDPDesktopThumbnails';
import { getMainImageImgixPropsForPhoto } from './helpers/imgix-props';
import { rememberPaintedHero } from './helpers/lastPaintedHero';
import usePDPDesktopHeroSizeProps from './helpers/usePDPDesktopHeroSizeProps';
import PDPProductIntroVideo from './PDPProductIntroVideo';
import { PDP_MAIN_IMAGE } from './specs/testIds';
import LinkOrFragment from '../../LinkOrFragment';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
const QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH = '100%';
const QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH = 570;

// Cap the SSR/first-paint main image width. The server renders a plain <img>
// (no srcset), and React Float preloads that exact URL as the LCP candidate.
// Without a width, imgix serves the source at native resolution, so an oversized
// source (e.g. the bondi-outdoor-double-sofa config image at 7366px / ~8MB)
// becomes a multi-second LCP. 1600px matches the studio hero default width and
// covers retina; the client react-imgix render upgrades via srcset after hydration.
const SSR_MAIN_IMAGE_MAX_WIDTH = 1600;
const dazeyLogo = 'https://joybird2.imgix.net/user-uploads-staging/JBxDaniDazey-Logo-Button.png';
const PDPDesktopHeroGalleryUI = ({
  campaign,
  configurationPhotos = [],
  isMechanicalOpen = null,
  toggleMechanicalOpen,
  cylindoFeatures,
  dimensionsImage,
  enableCrops = true,
  introVideoIds,
  isConfigFlipped = false,
  isCylindoAvailable,
  isCylindoConfigurationPhotos,
  isEditOrder = true,
  isVideoSelected,
  loading = false,
  mainPhotoLink,
  mode = 'pdp',
  pdpIsModularBYOProduct = false,
  productName,
  selectedPhoto,
  setIsVideoSelected,
  setSelectedPhoto,
  showDecideLaterOverlay,
  studioPhotos = [],
  resetGalleryImageTracking,
  viewGalleryImageEvent,
  dimensionShotUrls = [],
  dimensionShotsToShow = [],
  selectedDimensionShotIndex = 0,
  setSelectedDimensionShotIndex,
  shouldShowDimensionThumbnail = false
}) => {
  const {
    isMobile,
    isMobileOrTablet,
    isDesktop
  } = useScreen();
  const isQuickviewMode = mode === 'quickview';
  const isPdpMode = mode === 'pdp';
  const isBrowser = useIsBrowser();
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const {
    trackGalleryInteracted
  } = usePDPGalleryInteractedEvent();

  // Each opening is its own run of images, so the dedupe starts clean
  useEffect(() => {
    if (isFullScreenGalleryOpen) {
      resetGalleryImageTracking?.();
    }
  }, [isFullScreenGalleryOpen, resetGalleryImageTracking]);

  // Keep the hero <img> mounted through the Cylindo takeover: held at takeover,
  // above the viewer until it reports loaded, then hidden in place (CON-8543).
  const [isCylindoViewerReady, setIsCylindoViewerReady] = useState(false);
  const onCylindoViewerLoaded = useCallback(() => setIsCylindoViewerReady(true), []);
  const lastStaticHeroRef = useRef(null);

  // On a fresh deep-link load the first client render passes loading=true and
  // used to drop the SSR-painted hero. Recover its src from the DOM (CON-8543).
  const [ssrPaintedHeroSrc] = useState(() => typeof document === 'undefined' ? null : document.querySelector(`img[data-testid="${PDP_MAIN_IMAGE}"]`)?.currentSrc || null);
  const lastPaintedHeroSrcRef = useRef(null);
  const [isLiveHeroPainted, setIsLiveHeroPainted] = useState(false);

  // Also remembered at module scope so the Suspense fallbacks can re-show it
  // after React detaches this subtree (CON-8543).
  const onHeroImgLoad = useCallback(event => {
    const painted = event?.target?.currentSrc;
    if (painted) {
      lastPaintedHeroSrcRef.current = painted;
      rememberPaintedHero(painted);
    }
    setIsLiveHeroPainted(true);
    markLuxPageNavigationEnd();
  }, []);
  const brokenImageUrls = useBrokenImageUrls();
  const isStudioGallery = selectedPhoto?.type === 'studio';
  const isDimensionImg = selectedPhoto?.type === 'dimensional';
  const isConfigGallery = selectedPhoto?.type === 'configuration' || selectedPhoto?.type === 'cylindo';
  const isBuildYourOwnImg = selectedPhoto?.type === 'buildyourown';
  const photos = useMemo(() => {
    const allPhotos = isStudioGallery ? studioPhotos : pdpIsModularBYOProduct && isBuildYourOwnImg ? [selectedPhoto] : configurationPhotos;
    const validPhotos = allPhotos.filter(({
      src
    }) => {
      const cleanImageUrl = getImageUrlWithoutQuery(src);
      // Remove any broken images from the array passed to the gallery modal
      return !brokenImageUrls.includes(cleanImageUrl);
    });
    return validPhotos;
  }, [brokenImageUrls, configurationPhotos, pdpIsModularBYOProduct, isBuildYourOwnImg, isStudioGallery, selectedPhoto, studioPhotos]);
  const selectedPhotoIndex = Math.max(0, photos.findIndex(({
    src: src_0
  }) => src_0 === selectedPhoto?.src));
  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();

  // Pin the crop format for the same reason as ratioForCroppedImages below: letting
  // it follow useScreen's mobile-first default would change the image URL after
  // hydration and re-fetch the hero (CON-8543).
  const useMobileCropFormat = isPdpMode ? false : isMobileOrTablet;
  const ssrMainImageSrc = useMemo(() => {
    if (!selectedPhoto?.src) {
      return null;
    }

    // Never run a Cylindo URL through imgix - it already carries its own query string.
    if (selectedPhoto?.type === 'cylindo') {
      return selectedPhoto.src;
    }
    return convertImgixPropsToQueryParams({
      // width acts as a fallback cap; crop data (when present) can still
      // override `w` via convertImgixPropsToQueryParams' crop spread.
      width: SSR_MAIN_IMAGE_MAX_WIDTH,
      // Same imgix params the post-hydration JBProductImage render applies,
      // so the srcset upgrade only changes resolution, never composition.
      ...getProductImageBaseImgixParams({
        type: selectedPhoto.type,
        src: selectedPhoto.src,
        crops: selectedPhoto.cropData,
        enableCrops,
        isMobileOrTablet: useMobileCropFormat
      }),
      ...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops),
      ...mainPhotoSizeProps
    }, selectedPhoto.src, useMobileCropFormat);
  }, [selectedPhoto, enableCrops, mainPhotoSizeProps, useMobileCropFormat]);
  const introVideoId = introVideoIds && (isMobile ? introVideoIds.mobile : introVideoIds.desktop);

  // In pdp mode this gallery only displays at lg+, so the desktop ratio is
  // correct on the server too. Deriving it from useScreen would flip the hero
  // from 1:1 to 2:3 after hydration (CON-8543). Quickview shows on tablets, so
  // it keeps the viewport-driven value.
  const ratioForCroppedImages = isPdpMode || !isMobileOrTablet ? 2 / 3 : 1;
  const ratio = loading || enableCrops ? ratioForCroppedImages : 1 / 2;
  const isCylindoViewerActive = isCylindoAvailable && isCylindoConfigurationPhotos && !isStudioGallery && !isDimensionImg && !isVideoSelected;

  // Snapshot the hero right before the viewer takes over; heldHero renders from
  // this snapshot so the element survives the takeover with an unchanged src.
  useEffect(() => {
    if (!isCylindoViewerActive && ssrMainImageSrc) {
      lastStaticHeroRef.current = {
        photo: selectedPhoto,
        src: ssrMainImageSrc
      };
    }
  }, [isCylindoViewerActive, ssrMainImageSrc, selectedPhoto]);

  // Backstop: never hold the static hero for more than a few seconds if the viewer's
  // loaded event doesn't arrive.
  useEffect(() => {
    if (!isCylindoViewerActive || isCylindoViewerReady) {
      return undefined;
    }
    const timer = setTimeout(() => setIsCylindoViewerReady(true), 4000);
    return () => clearTimeout(timer);
  }, [isCylindoViewerActive, isCylindoViewerReady]);
  const heldHero = isCylindoViewerActive ? lastStaticHeroRef.current : null;
  const heroPhoto = heldHero ? heldHero.photo : selectedPhoto;
  const heroImgSrc = heldHero ? heldHero.src : ssrMainImageSrc;

  // Cylindo frame-1 must stay byte-identical to the viewer placeholder, so it
  // renders as a raw <img> that bypasses imgix (CON-8293).
  const isCylindoHeroImage = heroPhoto?.type === 'cylindo' && !!heroPhoto?.src;

  // While the viewer boots, the held hero sits above it in the same box; once
  // the viewer has painted it fades out in place, never unmounted. The fade
  // softens the framing change between config photo and frame-1 on deep links.
  const heroHoldClassNames = {
    'absolute inset-0 z-[1] pointer-events-none transition-opacity duration-500': !!heldHero,
    'opacity-0': !!heldHero && isCylindoViewerReady
  };
  const heroBaseSrc = heroPhoto?.src || null;
  const heldPrevHeroSrc = lastPaintedHeroSrcRef.current || (isPdpMode ? ssrPaintedHeroSrc : null);

  // Consider the hero freshly unpainted whenever the underlying picture changes,
  // with a backstop so a missed load event can never hold a stale image around.
  useEffect(() => {
    setIsLiveHeroPainted(false);
    const timer_0 = setTimeout(() => setIsLiveHeroPainted(true), 3000);
    return () => clearTimeout(timer_0);
  }, [heroBaseSrc]);

  // Hold the painted image over the incoming srcset re-fetch (same picture,
  // different width) - gated by isSamePicture so a thumbnail switch isn't held up.
  const isSamePicture = (a, b) => {
    if (!a || !b || typeof window === 'undefined') {
      return false;
    }
    try {
      return new URL(a, window.location.href).pathname === new URL(b, window.location.href).pathname;
    } catch {
      return false;
    }
  };
  const showRefetchHold = isPdpMode && !loading && !heldHero && !isLiveHeroPainted && isBrowser && !isCylindoHeroImage && !isDimensionImg && isSamePicture(heldPrevHeroSrc, heroBaseSrc);
  const showLoadingHeldHero = loading && isPdpMode && !!heldPrevHeroSrc;
  const spaceAround = 32 /* Photo top padding */ + 72 /* Photo bottom padding & thumbnails */ + 90; /* Key selling points */

  const maxGalleryWidth = useMemo(() => {
    if (isQuickviewMode) {
      return QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
    }
    return `calc((100vh - ${spaceAround}px) / ${ratio})`;
  }, [isQuickviewMode, ratio, spaceAround]);
  const galleryWidth = useMemo(() => {
    if (isQuickviewMode && !isDesktop) {
      return QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH;
    }
    if (isQuickviewMode) {
      return QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
    }
    return undefined;
  }, [isDesktop, isQuickviewMode]);
  const openGalleryModal = () => {
    setIsFullScreenGalleryOpen(true);
  };
  const closeGalleryModal = () => {
    setIsFullScreenGalleryOpen(false);
  };
  const isMainPhotoLinked = isQuickviewMode && mainPhotoLink;
  return <>
      <div className="mb-8 ml-auto mr-auto relative" style={{
      maxWidth: maxGalleryWidth,
      width: galleryWidth
    }}>
        <LinkOrFragment className={isMainPhotoLinked ? 'block focus-visible:outline-offset-4' : undefined} to={isMainPhotoLinked ? mainPhotoLink : undefined}>
          <RatioContainer className={classnames('bg-transparent border-0 focus:outline-0', {
          'PDPDesktopHeroGalleryUI__MainPhoto--mode-pdp': mode === 'pdp',
          'PDPDesktopHeroGalleryUI__MainPhoto--mode-quickview': mode === 'quickview',
          'flex justify-center': brokenImageUrls.includes(selectedPhoto?.src)
        })} ratio={ratio} onClick={() => {
          if (isPdpMode && (
          // If Cylindo is enabled for config gallery, only open full
          // screen gallery if studio gallery is selected
          !isCylindoConfigurationPhotos && isConfigGallery || isStudioGallery) && !isVideoSelected) {
            openGalleryModal();
            // The hero click is Control's second way into the Lifestyle
            // Gallery, alongside the +N tile (CON-8512)
            if (isStudioGallery) {
              trackGalleryInteracted(GALLERY_INTERACTION.LIFESTYLE_GALLERY_OPENED);
            }
          }
        }}>
            {!loading && (
          // With the Cylindo viewer active, keep rendering the (held) hero;
          // otherwise render Image only for studio/dimension images in "Cylindo Variant"
          heldHero ? true : (isCylindoAvailable && isCylindoConfigurationPhotos ? isStudioGallery || isDimensionImg : true) && !isVideoSelected && !!selectedPhoto && (!!selectedPhoto.src || isDimensionImg)) && (isDimensionImg && !heldHero ? <PDPDimensionShotViewer dimensionShots={dimensionShotsToShow} selectedDimensionShotIndex={selectedDimensionShotIndex} onIndexChange={setSelectedDimensionShotIndex} className="h-full w-full" {...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops)} {...mainPhotoSizeProps} data-testid={PDP_MAIN_IMAGE} lazy={false} priority onLoad={onHeroImgLoad} /> : (!isBrowser || isCylindoHeroImage || isPdpMode) &&
          // In pdp mode the hero stays this plain <img> for its whole
          // life: the post-hydration srcset upgrade re-fetches at another
          // width and visibly shifts the composition (CON-8543).
          heroPhoto?.src && !isDimensionImg ? <img className={classnames('h-full w-full object-contain object-center select-none', {
            'p-8': !isStudioGallery && !enableCrops,
            'scale-x-[-1]': !isStudioGallery && isConfigFlipped && !isDimensionImg,
            'hover:cursor-[unset]': !isStudioGallery && isCylindoConfigurationPhotos,
            'hover:cursor-zoom-in': mode === 'pdp'
          }, heroHoldClassNames)} src={heroImgSrc || heroPhoto.src} alt={productName ? String(productName) : ''} width={1600} height={2400} loading="eager" fetchPriority="high" decoding="async" data-testid={PDP_MAIN_IMAGE} onLoad={onHeroImgLoad} /> : <JBProductImage className={classnames('h-full w-full object-contain object-center select-none', {
            'p-8': !isStudioGallery && !enableCrops,
            'scale-x-[-1]': !isStudioGallery && isConfigFlipped && !isDimensionImg,
            'hover:cursor-[unset]': !isStudioGallery && isCylindoConfigurationPhotos,
            'hover:cursor-zoom-in': mode === 'pdp'
          }, heroHoldClassNames)} {...getMainImageImgixPropsForPhoto(heroPhoto, enableCrops)} {...mainPhotoSizeProps} data-testid={PDP_MAIN_IMAGE} lazy={false} priority onLoad={onHeroImgLoad} />)}

            {showRefetchHold && <img className="absolute inset-0 h-full w-full object-contain object-center select-none pointer-events-none z-[1]" src={heldPrevHeroSrc} alt="" aria-hidden="true" />}

            {!loading && isVideoSelected && introVideoId && <PDPProductIntroVideo videoId={introVideoId} />}

            {!loading && isCylindoAvailable && <div className={classnames('w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden', {
            invisible: !(isCylindoAvailable && isCylindoConfigurationPhotos) || isStudioGallery || isDimensionImg || isVideoSelected
          })}>
                <PDPCylindo3dViewer cylindoFeatures={cylindoFeatures} rotateIndex={isConfigGallery ? selectedPhotoIndex : -1} onViewerLoaded={onCylindoViewerLoaded} />
              </div>}

            {!loading && showDecideLaterOverlay && !(isStudioGallery || isVideoSelected) && <PDPDecideLaterOverlay />}

            {loading && (showLoadingHeldHero ? <img className="h-full w-full object-contain object-center select-none pointer-events-none" src={heldPrevHeroSrc} alt="" aria-hidden="true" /> : <Placeholder type="rect" loading />)}
          </RatioContainer>
        </LinkOrFragment>

        {!loading && pdpIsModularBYOProduct && !isStudioGallery && !isBuildYourOwnImg ? <div className="text-sm font-bold bg-[rgba(44,44,44,.7)] text-white left-[calc(50%_-_85px)] opacity-60 absolute text-center top-[40%] w-[190px] z-[2]">
            Sample configuration shown
          </div> : null}

        {campaign === 'barbie' && <img src={BarbieLogo} alt="barbie-logo" className="w-20 h-20 absolute top-12 left-4 z-50" />}

        {campaign === 'bipoc' && <img src={BipocLogo} alt="bipoc-owned-logo" className="w-20 h-20 absolute top-12 left-4 z-50" />}

        {campaign === 'dazey' && <JBImage src={dazeyLogo} className="absolute top-10 left-2 z-50" width={120} />}

        <PDPDesktopThumbnails isEditOrder={isEditOrder} configurationPhotos={configurationPhotos} isMechanicalOpen={isMechanicalOpen} toggleMechanicalOpen={toggleMechanicalOpen} dimensionsImage={dimensionsImage} shouldShowDimensionThumbnail={shouldShowDimensionThumbnail} dimensionShotUrls={dimensionShotUrls} enableCrops={enableCrops} pdpIsModularBYOProduct={pdpIsModularBYOProduct} introVideoId={introVideoId} isConfigFlipped={isConfigFlipped} isCylindoConfigurationPhotos={isCylindoConfigurationPhotos} isVideoSelected={isVideoSelected} loading={loading || !isBrowser} mode={mode} openModal={openGalleryModal} productName={productName} selectedPhoto={selectedPhoto} setSelectedPhoto={setSelectedPhoto} setIsVideoSelected={setIsVideoSelected} studioPhotos={studioPhotos} viewGalleryImageEvent={viewGalleryImageEvent} />

        {!loading && <div className="flex justify-between left-4 absolute right-4 top-4 z-[2]">
            <PDPGalleryLabelsAndViewingCount mode={mode} />
            {!isQuickviewMode && <PDPFavoriteButton />}
          </div>}
      </div>
      <BrowserOnly>
        <PDPDesktopFullScreenGallery2 isEditOrder={isEditOrder} isOpen={isFullScreenGalleryOpen} viewGalleryImageEvent={viewGalleryImageEvent}
      // The modal shows the configuration strip when that gallery is
      // selected; only studio photos are the Lifestyle Gallery
      imageLocation={isStudioGallery ? GALLERY_IMAGE_LOCATION.LIFESTYLE : GALLERY_IMAGE_LOCATION.CONFIGURATION} photos={photos} initialSelectedPhotoIndex={selectedPhotoIndex} onClose={closeGalleryModal} isStudioGallery={isStudioGallery} pdpIsModularBYOProduct={pdpIsModularBYOProduct} productName={productName} key={(productName || '').split(' ').join('-')} setSelectedPhoto={setSelectedPhoto} introVideoId={introVideoId} isVideoSelected={isVideoSelected} setIsVideoSelected={setIsVideoSelected} />
      </BrowserOnly>
    </>;
};
PDPDesktopHeroGalleryUI.propTypes = {
  configurationPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['buildyourown', 'configuration', 'cylindo', 'dimensional', 'studio'])
  })),
  isMechanicalOpen: PropTypes.bool,
  toggleMechanicalOpen: PropTypes.func,
  dimensionsImage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  }),
  isEditOrder: PropTypes.bool,
  enableCrops: PropTypes.bool,
  loading: PropTypes.bool,
  selectedPhoto: PropTypes.shape({
    type: PropTypes.oneOf(['buildyourown', 'configuration', 'cylindo', 'dimensional', 'studio']),
    src: PropTypes.string
  }),
  setSelectedPhoto: PropTypes.func,
  showDecideLaterOverlay: PropTypes.bool,
  studioPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired
  })),
  isConfigFlipped: PropTypes.bool,
  introVideoIds: PropTypes.shape({
    desktop: PropTypes.string,
    mobile: PropTypes.string
  }),
  setIsVideoSelected: PropTypes.func,
  isVideoSelected: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productName: PropTypes.string,
  isCylindoAvailable: PropTypes.bool,
  cylindoFeatures: PropTypes.objectOf(PropTypes.string),
  isCylindoConfigurationPhotos: PropTypes.bool,
  resetGalleryImageTracking: PropTypes.func,
  viewGalleryImageEvent: PropTypes.func,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  mainPhotoLink: PropTypes.string,
  campaign: PropTypes.string
};
export default PDPDesktopHeroGalleryUI;